import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  RiCalendarLine,
  RiMapPinLine,
  RiUserStarLine,
  RiFileTextLine,
  RiMicLine,
  RiGroupLine,
  RiAwardLine,
  RiGlobalLine,
  RiLightbulbLine,
  RiHeartPulseLine,
  RiTestTubeLine,
  RiBarChartLine,
  RiArrowRightSLine,
  RiMailLine,
  RiPhoneLine,
  RiTwitterLine,
  RiLinkedinLine,
  RiYoutubeLine,
} from 'react-icons/ri';

const AboutInformation = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { icon: <RiUserStarLine size={32} />, value: "500+", label: "Attendees" },
    { icon: <RiGroupLine size={32} />, value: "30+", label: "Countries" },
    { icon: <RiMicLine size={32} />, value: "50+", label: "Speakers" },
    { icon: <RiFileTextLine size={32} />, value: "200+", label: "Abstracts" },
  ];

  const objectives = [
    {
      icon: <RiLightbulbLine size={24} />,
      title: "Share Knowledge",
      description: "Exchange cutting-edge research and innovative ideas in immunology"
    },
    {
      icon: <RiHeartPulseLine size={24} />,
      title: "Advance Immunology",
      description: "Promote groundbreaking discoveries and therapeutic approaches"
    },
    {
      icon: <RiTestTubeLine size={24} />,
      title: "Foster Collaboration",
      description: "Connect researchers, clinicians, and industry professionals"
    },
    {
      icon: <RiBarChartLine size={24} />,
      title: "Future Directions",
      description: "Explore emerging trends and future challenges in immunology"
    }
  ];

  const speakers = [
    { name: "Dr. Sarah Johnson", title: "Nobel Laureate, Stanford University", image: "https://randomuser.me/api/portraits/women/68.jpg" },
    { name: "Prof. Michael Chen", title: "Harvard Medical School", image: "https://randomuser.me/api/portraits/men/32.jpg" },
    { name: "Dr. Elena Rodriguez", title: "Max Planck Institute", image: "https://randomuser.me/api/portraits/women/44.jpg" },
    { name: "Prof. James Wilson", title: "Oxford University", image: "https://randomuser.me/api/portraits/men/56.jpg" },
  ];

  const schedule = [
    { time: "08:00 - 09:00", title: "Registration & Breakfast", location: "Main Hall" },
    { time: "09:00 - 10:30", title: "Opening Ceremony & Keynote", location: "Plenary Hall" },
    { time: "10:30 - 11:00", title: "Coffee Break & Networking", location: "Exhibition Area" },
    { time: "11:00 - 13:00", title: "Technical Sessions", location: "Breakout Rooms" },
    { time: "13:00 - 14:30", title: "Lunch & Poster Session", location: "Dining Hall" },
    { time: "14:30 - 16:30", title: "Workshops & Panel Discussions", location: "Meeting Rooms" },
    { time: "16:30 - 17:00", title: "Awards Ceremony", location: "Plenary Hall" },
  ];

  return (
    <div style={{ background: "#f5f7fa", minHeight: "100vh" }}>
      {/* Hero Section with Animation */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{
          background: "linear-gradient(135deg, #0a2463 0%, #1e3a8a 50%, #3b82f6 100%)",
          color: "white",
          padding: "80px 40px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background bubbles */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, overflow: "hidden" }}>
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.1, scale: 1 }}
              transition={{ duration: 2, delay: i * 0.1, repeat: Infinity, repeatType: "reverse" }}
              style={{
                position: "absolute",
                width: Math.random() * 100 + 50,
                height: Math.random() * 100 + 50,
                borderRadius: "50%",
                background: "white",
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
        
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          style={{ position: "relative", zIndex: 2 }}
        >
          <h1 style={{ fontSize: "clamp(36px, 5vw, 56px)", marginBottom: 16, fontFamily: "DM Serif Display, serif" }}>
            About meetings21 2026
          </h1>
          <p style={{ fontSize: "clamp(16px, 2vw, 20px)", maxWidth: 800, margin: "0 auto", opacity: 0.95 }}>
            3rd World Summit & Expo on Immunology
          </p>
          <div style={{ marginTop: 32, display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <RiCalendarLine size={20} />
              <span>June 22–24, 2026</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <RiMapPinLine size={20} />
              <span>Rome, Italy</span>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "60px 24px" }}>
        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 24,
            marginBottom: 60,
          }}
        >
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.05, y: -5 }}
              style={{
                background: "white",
                padding: "32px 24px",
                borderRadius: 16,
                textAlign: "center",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                cursor: "pointer",
              }}
            >
              <div style={{ color: "#3b82f6", marginBottom: 16 }}>{stat.icon}</div>
              <div style={{ fontSize: 36, fontWeight: 800, color: "#0a2463", marginBottom: 8 }}>
                {stat.value}
              </div>
              <div style={{ color: "#666", fontSize: 14 }}>{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Conference Overview */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{
            background: "white",
            borderRadius: 20,
            padding: 48,
            marginBottom: 60,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}>
            <div>
              <span style={{ background: "#e0e7ff", color: "#3b82f6", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                WELCOME MESSAGE
              </span>
              <h2 style={{ fontSize: 32, marginTop: 16, marginBottom: 20, fontFamily: "DM Serif Display, serif" }}>
                Welcome to the Future of Immunology
              </h2>
              <p style={{ color: "#666", lineHeight: 1.8, marginBottom: 16 }}>
                The 3rd World Summit and Expo on Immunology (meetings21) brings together the brightest minds in immunology, 
                immunotherapy, and related fields. This premier event offers an unparalleled opportunity to explore cutting-edge 
                research, innovative therapies, and breakthrough technologies.
              </p>
              <p style={{ color: "#666", lineHeight: 1.8, marginBottom: 24 }}>
                With over 500 participants from 30+ countries, this summit is the perfect platform to network, collaborate, 
                and shape the future of immunological sciences.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/register')}
                style={{
                  background: "linear-gradient(135deg, #3b82f6, #1e3a8a)",
                  color: "white",
                  border: "none",
                  padding: "12px 32px",
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Register Now →
              </motion.button>
            </div>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600"
                alt="Conference"
                style={{ width: "100%", borderRadius: 16, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Objectives Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{ marginBottom: 60 }}
        >
          <h2 style={{ fontSize: 32, textAlign: "center", marginBottom: 16 }}>Conference Objectives</h2>
          <p style={{ textAlign: "center", color: "#666", marginBottom: 48, maxWidth: 600, margin: "0 auto 48px" }}>
            Our mission is to advance immunology through knowledge sharing and collaboration
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 24 }}>
            {objectives.map((obj, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -10 }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                style={{
                  background: "white",
                  padding: 32,
                  borderRadius: 16,
                  textAlign: "center",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                }}
              >
                <div style={{ color: "#3b82f6", marginBottom: 16 }}>{obj.icon}</div>
                <h3 style={{ fontSize: 20, marginBottom: 12 }}>{obj.title}</h3>
                <p style={{ color: "#666", fontSize: 14, lineHeight: 1.6 }}>{obj.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Speakers Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{ marginBottom: 60 }}
        >
          <h2 style={{ fontSize: 32, textAlign: "center", marginBottom: 48 }}>Keynote Speakers</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 32 }}>
            {speakers.map((speaker, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                style={{
                  background: "white",
                  borderRadius: 16,
                  overflow: "hidden",
                  textAlign: "center",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                }}
              >
                <img
                  src={speaker.image}
                  alt={speaker.name}
                  style={{ width: "100%", height: 250, objectFit: "cover" }}
                />
                <div style={{ padding: 20 }}>
                  <h3 style={{ fontSize: 18, marginBottom: 8 }}>{speaker.name}</h3>
                  <p style={{ color: "#666", fontSize: 13 }}>{speaker.title}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Schedule Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{
            background: "white",
            borderRadius: 20,
            padding: 48,
            marginBottom: 60,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <h2 style={{ fontSize: 32, textAlign: "center", marginBottom: 48 }}>Conference Schedule</h2>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f3f4f6", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: 16, textAlign: "left" }}>Time</th>
                  <th style={{ padding: 16, textAlign: "left" }}>Session</th>
                  <th style={{ padding: 16, textAlign: "left" }}>Location</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((item, i) => (
                  <motion.tr
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    viewport={{ once: true }}
                    style={{ borderBottom: "1px solid #e5e7eb" }}
                  >
                    <td style={{ padding: 16, fontWeight: 600 }}>{item.time}</td>
                    <td style={{ padding: 16 }}>{item.title}</td>
                    <td style={{ padding: 16, color: "#666" }}>{item.location}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Venue Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{
            background: "linear-gradient(135deg, #1e3a8a, #3b82f6)",
            borderRadius: 20,
            padding: 48,
            color: "white",
            marginBottom: 60,
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}>
            <div>
              <h2 style={{ fontSize: 32, marginBottom: 16 }}>Venue Information</h2>
              <h3 style={{ fontSize: 24, marginBottom: 12 }}>Hotel American Palace Eur</h3>
              <p style={{ marginBottom: 16, opacity: 0.95 }}>
                Via Laurentina, 554, 00143 Rome, Italy
              </p>
              <p style={{ marginBottom: 24, opacity: 0.9, lineHeight: 1.6 }}>
                Located in the prestigious EUR business district, the Hotel American Palace Eur offers 
                state-of-the-art facilities, comfortable accommodation, and excellent transport links 
                to Rome's city center and Fiumicino Airport.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                style={{
                  background: "white",
                  color: "#3b82f6",
                  border: "none",
                  padding: "10px 24px",
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
                onClick={() => window.open("https://maps.google.com", "_blank")}
              >
                View on Map →
              </motion.button>
            </div>
            <motion.div
              initial={{ scale: 0.9 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <iframe
                title="venue"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2970.123456789012!2d12.456789!3d41.832123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x13258a123456789%3A0x123456789abcdef!2sHotel%20American%20Palace%20Eur!5e0!3m2!1sen!2sit!4v1234567890123!5m2!1sen!2sit"
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: 12 }}
                allowFullScreen=""
                loading="lazy"
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{
            background: "white",
            borderRadius: 20,
            padding: 48,
            textAlign: "center",
          }}
        >
          <h2 style={{ fontSize: 32, marginBottom: 16 }}>Get in Touch</h2>
          <p style={{ color: "#666", marginBottom: 32, maxWidth: 600, margin: "0 auto 32px" }}>
            Have questions? Contact our conference team for assistance
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 32, flexWrap: "wrap", marginBottom: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <RiMailLine size={20} color="#3b82f6" />
              <span>info@meetings21.com</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <RiPhoneLine size={20} color="#3b82f6" />
              <span>+39 06 1234 5678</span>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
            <motion.a
              whileHover={{ scale: 1.1 }}
              href="#"
              style={{ background: "#f3f4f6", padding: 10, borderRadius: "50%", display: "inline-flex", cursor: "pointer" }}
            >
              <RiTwitterLine size={20} />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.1 }}
              href="#"
              style={{ background: "#f3f4f6", padding: 10, borderRadius: "50%", display: "inline-flex", cursor: "pointer" }}
            >
              <RiLinkedinLine size={20} />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.1 }}
              href="#"
              style={{ background: "#f3f4f6", padding: 10, borderRadius: "50%", display: "inline-flex", cursor: "pointer" }}
            >
              <RiYoutubeLine size={20} />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutInformation;